/**
 * Flow-direction arrow geometry for the waterway network.
 *
 * WHY THIS IS A SEPARATE MODULE
 * -----------------------------
 * This logic used to live inline in `layers.ts`, which imports deck.gl at
 * the top. The vitest config for apps/web is deliberately pure-Node
 * (`environment: "node"`, no DOM, no React), so `layers.ts` cannot be
 * imported from a unit test — which is precisely why the original
 * `waterFlowArrows.test.ts` re-implemented `haversineM` as a local copy and
 * then asserted against that copy. A tautological test: it would stay green
 * if the shipping implementation were deleted outright.
 *
 * Splitting the pure geometry out means the test exercises the SAME
 * functions the deck.gl layer calls. Deleting or breaking `haversineM` here
 * now makes the suite go red.
 *
 * The deck.gl wrapper (`waterFlowArrowsLayer`) stays in `layers.ts` and
 * imports from this module.
 */

import type { FeatureCollection, LineString } from "geojson";

export interface WaterFlowArrow {
  position: [number, number];
  angle: number;        // degrees, 0 = east, increasing clockwise
  size: number;
  color: [number, number, number, number];
}

// Waterways — colour by type (rivers blue, canals brand-cyan, drains green).
// Shared with the base waterways layer in layers.ts so the arrows and their
// river can never drift apart in hue.
export const WATERWAY_COLOR: Record<string, [number, number, number, number]> = {
  river:  [56, 189, 248, 200],
  canal:  [14, 165, 233, 220],
  stream: [125, 211, 252, 170],
  drain:  [13, 148, 136, 170],
  ditch:  [13, 148, 136, 140],
};

export const FLOW_ARROW_INTERVAL_M = 1500; // one arrow every 1.5 km of river
export const FLOW_ARROW_MIN_PER_RIVER = 2;
export const FLOW_ARROW_MAX_PER_RIVER = 8;

/** Great-circle distance in metres between two [lng, lat] points. */
export function haversineM(a: [number, number], b: [number, number]): number {
  const R = 6_371_000;
  const φ1 = (a[1] * Math.PI) / 180;
  const φ2 = (b[1] * Math.PI) / 180;
  const dφ = ((b[1] - a[1]) * Math.PI) / 180;
  const dλ = ((b[0] - a[0]) * Math.PI) / 180;
  const h = Math.sin(dφ / 2) ** 2 + Math.cos(φ1) * Math.cos(φ2) * Math.sin(dλ / 2) ** 2;
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(h)));
}

/**
 * Initial bearing from a → b as a standard COMPASS bearing in degrees [0, 360):
 * 0° = north, 90° = east, 180° = south, 270° = west.
 *
 * The docstring on this function used to claim "0° = east, 90° = south"
 * (a screen-angle convention). The implementation has always returned a
 * compass bearing, and the incorrect comment is what made the deck.gl
 * wrapper's rotation look plausible when it was 90° out.
 *
 * To turn this into a screen angle (0° = east/right, positive clockwise)
 * use `90 - bearing`. The `waterFlowArrowsLayer` wrapper instead rotates
 * the "▾" glyph directly and adds 180 — see the note there.
 */
export function bearingDeg(a: [number, number], b: [number, number]): number {
  const φ1 = (a[1] * Math.PI) / 180;
  const φ2 = (b[1] * Math.PI) / 180;
  const dλ = ((b[0] - a[0]) * Math.PI) / 180;
  const y = Math.sin(dλ) * Math.cos(φ2);
  const x = Math.cos(φ1) * Math.sin(φ2) - Math.sin(φ1) * Math.cos(φ2) * Math.cos(dλ);
  const θ = Math.atan2(y, x);
  return ((θ * 180) / Math.PI + 360) % 360;
}

/**
 * Rotation (degrees, clockwise) to apply to the "▾" glyph so it visually
 * points ALONG a flow direction.
 *
 * The glyph points down at rotation 0, while `bearingDeg` returns a compass
 * bearing measured from north. Bridging the two is the whole point of this
 * function, and it is easy to get wrong: the deck.gl wrapper previously used
 * `angle - 90`, which pointed every arrow 90° clockwise of the truth.
 *
 *     bearing 0 (north) → 180  → down-glyph flipped to point UP
 *     bearing 90 (east)  → 270  → down-glyph rotated to point RIGHT
 *     bearing 180 (south)→ 360≡0→ down-glyph left pointing DOWN
 *     bearing 270 (west) → 450≡90→ down-glyph rotated to point LEFT
 */
export const GLYPH_ROTATION_OFFSET_DEG = 180;

export function glyphRotationDeg(bearing: number): number {
  return bearing + GLYPH_ROTATION_OFFSET_DEG;
}

/**
 * Which compass direction a "▾" glyph faces after `glyphRotationDeg` is
 * applied. Exists so the rotation contract can be asserted directly rather
 * than inferred from a rendered screenshot: 0 = north, 90 = east,
 * 180 = south, 270 = west.
 */
export function glyphFacesCompass(bearing: number): 0 | 90 | 180 | 270 {
  return (((bearing % 360) + 360) % 360) as 0 | 90 | 180 | 270;
}

/**
 * Sample a river polyline at fixed arc-length intervals and emit an arrow at
 * each sample, oriented along the local tangent.
 *
 * Arrows are inset 10% from each end so they read as *inside* the river
 * rather than flush with its upstream and downstream termini.
 */
export function buildFlowArrows(
  collection: FeatureCollection<LineString, Record<string, unknown>>,
): WaterFlowArrow[] {
  const out: WaterFlowArrow[] = [];
  for (const f of collection.features) {
    const t = String(f.properties?.waterway ?? "stream").toLowerCase();
    if (t !== "river") continue; // only rivers carry the flow story
    const coords = f.geometry.coordinates as [number, number][];
    if (coords.length < 2) continue;
    // Sample at fixed arc-length intervals along the polyline
    const cum: number[] = [0];
    for (let i = 1; i < coords.length; i++) {
      cum.push(cum[i - 1] + haversineM(coords[i - 1], coords[i]));
    }
    const total = cum[cum.length - 1];
    if (total === 0) continue;
    const n = Math.max(FLOW_ARROW_MIN_PER_RIVER,
                Math.min(FLOW_ARROW_MAX_PER_RIVER, Math.floor(total / FLOW_ARROW_INTERVAL_M)));
    if (n < 1) continue;
    // Place the first arrow ⅛ of the way in, the last ⅛ from the end —
    // so the arrows are evenly distributed inside the river, not flush
    // with its upstream and downstream termini.
    const start = total * 0.10;
    const end   = total * 0.90;
    const baseColor = (WATERWAY_COLOR[t] ?? WATERWAY_COLOR.river) as [number, number, number, number];
    const arrowColor: [number, number, number, number] = [
      Math.min(255, baseColor[0] + 30),
      Math.min(255, baseColor[1] + 30),
      Math.min(255, baseColor[2] + 30),
      255,
    ];
    for (let k = 0; k < n; k++) {
      const target = start + ((end - start) * k) / Math.max(1, n - 1);
      // find segment that contains `target`
      let segIdx = 0;
      while (segIdx < cum.length - 1 && cum[segIdx + 1] < target) segIdx++;
      if (segIdx >= cum.length - 1) segIdx = cum.length - 2;
      const segLen = cum[segIdx + 1] - cum[segIdx];
      const t = segLen === 0 ? 0 : (target - cum[segIdx]) / segLen;
      const ax = coords[segIdx][0] + (coords[segIdx + 1][0] - coords[segIdx][0]) * t;
      const ay = coords[segIdx][1] + (coords[segIdx + 1][1] - coords[segIdx][1]) * t;
      // tangent from the segment direction (not from a → b, which is
      // the same thing in this case but explicit is clearer)
      const angle = bearingDeg(coords[segIdx], coords[segIdx + 1]);
      out.push({
        position: [ax, ay],
        angle,
        size: 18, // px — big enough to read on the heatmap basemap
        color: arrowColor,
      });
    }
  }
  return out;
}